/**
 * Fixture loader — serves JSON fixture files from /fixtures/ on demand.
 *
 * `initFixtures()` registers the fixture *names* (from the MSW endpoint
 * registry) so group lookups work; nothing is downloaded until a story or a
 * mocked API call first asks for a file. The fixture set is ~270 MB on disk
 * and most stories need none of it.
 *
 * Data wrappers use: getFixture<T>("shipments/get-shipment-by-id")
 * Keyboard: ArrowRight / ArrowLeft to cycle
 */

import { fixtureEndpoints } from "../Mock/endpoints";

/** Every fixture file the browser can serve, across both static roots. */
function registeredFixtureFiles(): string[] {
  const files = new Set<string>();
  for (const endpoint of fixtureEndpoints) {
    for (const fixture of endpoint.fixtures) files.add(fixture);
  }
  return [...files];
}

interface FixtureFile {
  filename: string;
  data: unknown;
}

export interface FixtureGroup {
  name: string;
  files: FixtureFile[];
}

// Group files by prefix: strip trailing digits before .json
function getGroupName(filename: string): string {
  return filename.replace(/\.json$/, "").replace(/\d+$/, "");
}

// ── Cache ────────────────────────────────────────────────────────

const cache = new Map<string, unknown>();
const rawCache = new Map<string, string>();
const inFlight = new Map<string, Promise<unknown>>();
const groups: Record<string, FixtureGroup> = {};
let initialized = false;

/** Seed a fixture directly into the cache (used by test setup to bypass fetch). */
export function seedFixture(filename: string, data: unknown): void {
  cache.set(filename, data);
}

/** Seed a fixture as a raw JSON string — parsed lazily on first access. */
export function seedFixtureRaw(filename: string, raw: string): void {
  rawCache.set(filename, raw);
}

/**
 * Set a resolver function for on-demand fixture loading. Registers filenames
 * and a resolver that is called only when getFixture() needs a file: the test
 * setup reads from disk, the browser fetches synchronously.
 */
let externalResolver: ((filename: string) => unknown) | undefined;

export function setFixtureResolver(
  filenames: string[],
  resolver: (filename: string) => unknown,
): void {
  externalResolver = resolver;
  // Register filenames so buildGroups() knows about them
  for (const filename of filenames) {
    // Don't load data yet — resolver will be called on demand
    if (!cache.has(filename) && !rawCache.has(filename)) {
      rawCache.set(filename, ""); // placeholder so buildGroups includes it
    }
  }
}

/** Mark fixtures as initialized after seeding (used by test setup to prevent fetch calls). */
export function markFixturesInitialized(): void {
  buildGroups();
  initialized = true;
}

/** @internal — reset all module state between tests */
export function __resetForTesting(): void {
  cache.clear();
  rawCache.clear();
  inFlight.clear();
  for (const key of Object.keys(groups)) delete groups[key];
  initialized = false;
  externalResolver = undefined;
}

function resolveData(filename: string): unknown {
  if (cache.has(filename)) return cache.get(filename);

  // Try external resolver first (loads the one file on demand)
  if (externalResolver) {
    const data = externalResolver(filename);
    if (data) {
      cache.set(filename, data);
      return data;
    }
  }

  // Fall back to raw cache (raw JSON string → parse)
  const raw = rawCache.get(filename);
  if (raw && raw.length > 0) {
    const data = JSON.parse(raw);
    cache.set(filename, data);
    return data;
  }
  return undefined;
}

function buildGroups() {
  // Clear and rebuild from cache + rawCache
  for (const key of Object.keys(groups)) delete groups[key];

  // Collect all known filenames (parsed + raw)
  const allFilenames = new Set([...cache.keys(), ...rawCache.keys()]);

  for (const filename of allFilenames) {
    const groupName = getGroupName(filename);
    if (!groups[groupName]) {
      groups[groupName] = { name: groupName, files: [] };
    }
    // Use a getter-like pattern: data resolves lazily
    groups[groupName].files.push({
      filename,
      get data() {
        return resolveData(filename);
      },
    });
  }

  for (const group of Object.values(groups)) {
    group.files.sort((a, b) => a.filename.localeCompare(b.filename));
  }
}

/**
 * Fixture static-file roots, tried in order. `/fixtures` holds real production
 * exports; `/fixtures-synthetic` holds hand-authored fake data for modules whose
 * real data is too sensitive to commit (People). Real wins on a name clash.
 */
const FIXTURE_URL_ROOTS = ["/fixtures", "/fixtures-synthetic"];

/** Fetch a fixture JSON by relative filename, falling back across roots. */
export async function fetchFixtureJson(filename: string): Promise<unknown> {
  for (const root of FIXTURE_URL_ROOTS) {
    const resp = await fetch(`${root}/${filename}`);
    // resp.ok alone can't distinguish "found the fixture" from "got the app
    // shell": the dev server's SPA fallback answers ANY unknown path with
    // index.html and a 200, which used to stop the chain at /fixtures and
    // fail every synthetic-only fixture (breaking their MSW handlers).
    // Require a JSON content-type before accepting a root's answer.
    const contentType = resp.headers.get("content-type") ?? "";
    if (resp.ok && contentType.includes("json")) return resp.json();
  }
  throw new Error(`Fixture not found in any root: ${filename}`);
}

/**
 * Fetch a fixture synchronously, for getFixture(). Stories call it while
 * their module is still evaluating, before any promise could resolve, so a
 * fixture that is not cached yet has to be fetched on the spot. Synchronous
 * XHR is deprecated on the main thread but every browser we run Storybook in
 * still supports it, and this path only runs in Storybook and mock-data mode.
 * It cannot deadlock with the mock service worker: the browser does not route
 * a synchronous request through a service worker at all (its resource timing
 * shows workerStart 0 while the worker controls the page), so the request goes
 * straight to the static file server. Same JSON content-type check as
 * fetchFixtureJson, for the same reason.
 */
function fetchFixtureJsonSync(filename: string): unknown {
  for (const root of FIXTURE_URL_ROOTS) {
    const request = new XMLHttpRequest();
    request.open("GET", `${root}/${filename}`, false);
    request.send();
    const contentType = request.getResponseHeader("content-type") ?? "";
    if (request.status === 200 && contentType.includes("json")) {
      return JSON.parse(request.responseText);
    }
  }
  throw new Error(`Fixture not found in any root: ${filename}`);
}

/** Fetch a fixture once and cache it; concurrent callers share one request. */
export async function loadFixture(filename: string): Promise<unknown> {
  if (cache.has(filename)) return cache.get(filename);
  let pending = inFlight.get(filename);
  if (!pending) {
    pending = (async () => {
      try {
        const data = await fetchFixtureJson(filename);
        cache.set(filename, data);
        return data;
      } finally {
        inFlight.delete(filename);
      }
    })();
    inFlight.set(filename, pending);
  }
  return pending;
}

// ── Page → group mapping ────────────────────────────────────────

const pageGroupMap: Record<string, string> = {
  "/": "shipments/get-active-shipments",
  "/active-shipments": "shipments/get-active-shipments",
  "/view-shipment": "shipments/get-shipment-by-id",
  "/view-company": "companies/view-company-data",
  "/list-companies": "companies/list-companies",
  "/claims": "shipments/get-claims",
  "/operational-tasks": "shipments/shipments-with-operational-tasks",
  "/carrier-pro-number-blocks": "carrier-pro-block/get-all-carrier-pro-block",
  "/view-api-keys": "api-keys/list-all-api-keys",
  "/experiments": "experiments/list-experiments",
  "/recent-manual-quotes": "shipments/recent-manual-quotes",
  "/task-queue": "queue/list-queue-tasks",
  "/signals": "signals/get-signals",
  // Alarms merged into the Signals page (ENG-3343)
  "/view-alarms": "signals/get-signals",
};

/**
 * Register every known fixture so getFixture() can find its group. Loads no
 * data: each file is fetched the first time something reads it.
 */
export function initFixtures(): void {
  if (initialized) return;

  // Under vitest the setup file registers fixtures from disk instead.
  if (typeof process !== "undefined" && process.env?.["VITEST"]) {
    return;
  }

  setFixtureResolver(registeredFixtureFiles(), fetchFixtureJsonSync);
  markFixturesInitialized();
}

// ── Fixture index (URL param > localStorage > 0) ───────────────

function getCurrentPage(): string {
  return window.location.pathname;
}

function storageKey(page: string): string {
  return `fixture:${page}`;
}

function getFixtureIndex(): number {
  const urlVal = new URLSearchParams(window.location.search).get("fixture");
  if (urlVal !== null) return Number(urlVal);
  const stored = localStorage.getItem(storageKey(getCurrentPage()));
  return stored !== null ? Number(stored) : 0;
}

function saveFixtureIndex(index: number): void {
  localStorage.setItem(storageKey(getCurrentPage()), String(index));
}

/**
 * Get the current fixture data for a group. Synchronous: the file is fetched
 * on first use, so a story pays only for the fixtures it reads.
 */
export function getFixture<T>(groupName: string): T {
  const group = groups[groupName];
  if (!group || group.files.length === 0) {
    throw new Error(
      `No fixture group found: "${groupName}". Was initFixtures() called?`,
    );
  }
  const raw = getFixtureIndex();
  const idx = raw >= 0 && raw < group.files.length ? raw : 0;
  return group.files[idx]!.data as T;
}

/** Get the fixture group relevant to the current page */
export function getCurrentPageGroup(): FixtureGroup | null {
  const groupName = pageGroupMap[window.location.pathname];
  if (!groupName) return null;
  return groups[groupName] ?? null;
}

/** Get info about the current fixture selection for the active page */
export function getCurrentFixtureInfo(): {
  group: FixtureGroup;
  index: number;
  total: number;
  filename: string;
} | null {
  const group = getCurrentPageGroup();
  if (!group || group.files.length <= 1) return null;
  const raw = getFixtureIndex();
  const idx = raw >= 0 && raw < group.files.length ? raw : 0;
  return {
    group,
    index: idx,
    total: group.files.length,
    filename: group.files[idx]!.filename,
  };
}

/** Navigate to a different fixture variant (reloads the page) */
export function navigateFixture(delta: number): void {
  const group = getCurrentPageGroup();
  if (!group || group.files.length <= 1) return;
  const current = Math.max(
    0,
    Math.min(getFixtureIndex(), group.files.length - 1),
  );
  const next = (current + delta + group.files.length) % group.files.length;
  saveFixtureIndex(next);
  const url = new URL(window.location.href);
  url.searchParams.set("fixture", String(next));
  window.location.href = url.toString();
}

/** Get all groups that have multiple fixture files */
export function getMultiFixtureGroups(): FixtureGroup[] {
  return Object.values(groups).filter((g) => g.files.length > 1);
}
